import { invalidateBoard } from '../state/scratch';

type Entry =
  | { status: 'loading' }
  | { status: 'ready'; image: HTMLImageElement }
  | { status: 'error' };

/** Soft cap so switching boards does not retain every decoded bitmap forever. */
const CACHE_LIMIT = 64;

const cache = new Map<string, Entry>();

let invalidateScheduled = false;

function scheduleInvalidate(): void {
  if (invalidateScheduled) return;
  invalidateScheduled = true;
  requestAnimationFrame(() => {
    invalidateScheduled = false;
    invalidateBoard();
  });
}

function touch(src: string, entry: Entry): void {
  // Map iteration order is insertion order — re-insert to mark as most-recent.
  cache.delete(src);
  cache.set(src, entry);
  while (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

/**
 * Non-blocking image resolution for the render loop.
 *
 * The renderer cannot await anything, so it asks for an image and either gets
 * one or draws a placeholder. When a load finishes, the cache schedules a
 * repaint, which is why this module owns the request rather than a React effect.
 */
export function getImage(src: string): HTMLImageElement | null {
  const entry = cache.get(src);
  if (entry) {
    touch(src, entry);
    return entry.status === 'ready' ? entry.image : null;
  }

  cache.set(src, { status: 'loading' });
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.decoding = 'async';
  image.onload = () => {
    if (!cache.has(src)) return;
    touch(src, { status: 'ready', image });
    scheduleInvalidate();
  };
  image.onerror = () => {
    if (!cache.has(src)) return;
    touch(src, { status: 'error' });
    scheduleInvalidate();
  };
  image.src = src;
  return null;
}

export function imageFailed(src: string): boolean {
  return cache.get(src)?.status === 'error';
}

/** Drop decoded bitmaps when leaving a board so RAM does not accumulate. */
export function clearImageCache(): void {
  cache.clear();
}

/** Preload before an export so no placeholder ends up in the output. */
export function preloadImages(sources: readonly string[]): Promise<void> {
  return Promise.all(
    sources.map(
      (src) =>
        new Promise<void>((resolve) => {
          const entry = cache.get(src);
          if (entry?.status === 'ready' || entry?.status === 'error') return resolve();
          const image = new Image();
          image.crossOrigin = 'anonymous';
          const done = () => resolve();
          image.onload = () => {
            touch(src, { status: 'ready', image });
            done();
          };
          image.onerror = () => {
            touch(src, { status: 'error' });
            done();
          };
          image.src = src;
        }),
    ),
  ).then(() => undefined);
}
